import React, { useEffect, useRef, useState } from 'react';
import { i18n } from '../../i18n/i18n';
import {
  formatClock, isFinished, isRunning, isStarted, newStepTimer, pauseTimer, remainingMs, resetTimer, startTimer,
} from '../../utils/stepTimer';
import { getTimerSound, playTimerSound, unlockTimerAudio } from '../../utils/timerSounds';

/**
 * The timer on a timed step (2026-10-19, Jens): ▶ Start, a big countdown with Pause and Start over,
 * and at zero the person's "time's up" sound, a buzz (where the phone allows it) and a clear
 * "Time's up!". Ticking the step stays a separate tap. Mount it with key={step id} so each step
 * starts fresh.
 */
const StepTimer: React.FC<{ seconds: number; personId?: string | null }> = ({ seconds, personId }) => {
  const [timer, setTimer] = useState(() => newStepTimer(seconds));
  const [now, setNow] = useState(() => Date.now());
  const rang = useRef(false);

  const running = isRunning(timer, now);
  const done = timer.endsAt !== null && isFinished(timer, now);

  // Tick while running; also catch up at once when the app comes back from the background.
  useEffect(() => {
    if (timer.endsAt === null) return;
    const tick = () => setNow(Date.now());
    const id = window.setInterval(tick, 250);
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { window.clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
  }, [timer.endsAt]);

  useEffect(() => {
    if (!done || rang.current) return;
    rang.current = true;
    playTimerSound(getTimerSound(personId));
    try { navigator.vibrate?.([300, 150, 300, 150, 300]); } catch { /* not on iPhone */ }
  }, [done, personId]);

  const start = () => {
    unlockTimerAudio(); // the tap allows the sound later
    rang.current = false;
    const t = Date.now();
    setNow(t);
    setTimer((prev) => startTimer(isFinished(prev, t) ? resetTimer(prev) : prev, t));
  };
  const pause = () => { const t = Date.now(); setNow(t); setTimer((prev) => pauseTimer(prev, t)); };
  const restart = () => { rang.current = false; setNow(Date.now()); setTimer((prev) => resetTimer(prev)); };

  const left = remainingMs(timer, now);
  const progress = timer.totalMs > 0 ? 1 - left / timer.totalMs : 0;
  const pill = 'inline-flex items-center justify-center gap-2 min-h-[52px] px-6 rounded-full font-bold text-lg border-2';

  if (!isStarted(timer)) {
    return (
      <div className="mt-5 flex justify-center lg:justify-start">
        <button type="button" onClick={start} className={`${pill} bg-kid-card dark:bg-kid-card-dark border-kid-green text-kid-green-shadow dark:text-green-200`}>
          <span aria-hidden>⏱</span> {i18n.t('timer.start')} <span className="tabular-nums">{formatClock(timer.totalMs)}</span>
        </button>
      </div>
    );
  }

  return (
    <div
      className={`mt-5 rounded-kid-card border-2 p-4 text-center ${done
        ? 'border-kid-green bg-kid-green-bg dark:bg-green-900/40 animate-pulse'
        : 'border-kid-border dark:border-kid-border-dark bg-kid-card dark:bg-kid-card-dark'}`}
    >
      <p role="timer" aria-live="off" className="font-kid-heading font-extrabold text-6xl md:text-7xl tabular-nums leading-none">
        {formatClock(left)}
      </p>
      <div className="mt-3 h-3 rounded-full bg-kid-chip dark:bg-kid-chip-dark overflow-hidden" aria-hidden>
        <div className="h-full bg-kid-green transition-[width] duration-200" style={{ width: `${Math.min(100, Math.max(0, progress * 100))}%` }} />
      </div>
      {done && (
        <p aria-live="assertive" className="mt-3 font-kid-heading font-extrabold text-2xl md:text-3xl">⏰ {i18n.t('timer.times_up')}</p>
      )}
      <div className="mt-3 flex flex-wrap justify-center gap-2">
        {!done && (running ? (
          <button type="button" onClick={pause} className={`${pill} border-kid-border-strong dark:border-kid-border-strong-dark`}>
            <span aria-hidden>⏸</span> {i18n.t('timer.pause')}
          </button>
        ) : (
          <button type="button" onClick={start} className={`${pill} border-kid-green text-kid-green-shadow dark:text-green-200`}>
            <span aria-hidden>▶</span> {i18n.t('timer.resume')}
          </button>
        ))}
        <button type="button" onClick={restart} className={`${pill} border-kid-border-strong dark:border-kid-border-strong-dark`}>
          <span aria-hidden>↺</span> {i18n.t('timer.restart')}
        </button>
      </div>
      {/* A web page can't override the phone's volume (2026-10-27); real alarms come with the iOS/Android apps. */}
      {!done && <p className="mt-3 text-sm text-kid-text-muted dark:text-kid-text-muted-dark">🔈 {i18n.t('timer.volume_note')}</p>}
    </div>
  );
};

export default StepTimer;
